import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Button, Card, Empty, Tag, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  CarOutlined,
  CheckOutlined,
  FireOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  WarningOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import logoNdoe from '../../assets/logo_doe-removebg-preview.png';
import config from '../../config';
// Blocs partagés avec l'Accueil admin (mêmes conventions V4) : recettes par
// cliente (période choisie), objectif du mois, balance âgée, marge brute.
import { RecettesClientes, ObjectifMois, BalanceAgee, MargeBrute } from '../rightbar/DashboardAdmin';
import '../rightbar/dashboardAdmin.css';
import { SalesTrendChart, TodayModelChart } from './MobileCharts';
import MobileStockHealth from './MobileStockHealth';
import { BottomNavigation } from './components/mobile/BottomNavigation';
import { SkeletonCard } from './components/mobile/SkeletonCard';
import './mobileCockpit.css';
import './mobileCockpitInsights.css';

const ZONE = 'Africa/Kinshasa';
// Le backend garde un micro-cache (overview/insights 60 s, rotation 300 s) : un polling
// plus espacé suffit, et il est suspendu quand l'onglet est caché.
const POLL_MS = 90000;
const PERIODS = [
  { key: 'today', label: "Aujourd'hui" },
  { key: 'yesterday', label: 'Hier' },
  { key: 'week', label: '7 jours' },
  { key: 'month', label: 'Ce mois' },
];
const EMPTY = { meta: {}, summary: { commands: {}, in_transit: {}, pending_outcome: {}, confirmed_sales: {}, returns: {}, exchanges: {}, low_stock: {} }, activities: [], low_stock: [] };
const EMPTY_ROTATION = { meta: {}, summary: {}, items: [] };
const EMPTY_INSIGHTS = { meta: {}, today: { variants: [], models: [] }, period_trend: [] };
const n = (input) => Number(input || 0);
const integer = (input) => n(input).toLocaleString('fr-FR');
const usd = (input) => `${integer(input)} USD`;

const kinshasaDay = () => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const item = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return `${item.year}-${item.month}-${item.day}`;
};
const addDays = (day, delta) => {
  const [year, month, date] = day.split('-').map(Number);
  const value = new Date(Date.UTC(year, month - 1, date));
  value.setUTCDate(value.getUTCDate() + delta);
  return value.toISOString().slice(0, 10);
};
const rangeFor = (key) => {
  const end = kinshasaDay();
  if (key === 'yesterday') { const yesterday = addDays(end, -1); return { start_date: yesterday, end_date: yesterday }; }
  if (key === 'week') return { start_date: addDays(end, -6), end_date: end };
  if (key === 'month') return { start_date: `${end.slice(0, 8)}01`, end_date: end };
  return { start_date: end, end_date: end };
};
const time = (value) => value ? new Intl.DateTimeFormat('fr-FR', { timeZone: ZONE, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '';
const clockTime = (value) => new Intl.DateTimeFormat('fr-FR', { timeZone: ZONE, hour: '2-digit', minute: '2-digit' }).format(new Date(value));
const activityStyle = (item) => {
  if (item.event_kind === 'sale') return ['is-sale', 'Vente confirmée'];
  if (item.event_kind === 'reception') return ['is-receipt', 'Réception'];
  const label = String(item.type_mouvement || '').toLowerCase();
  if (label.includes('retour')) return ['is-return', 'Retour'];
  if (label.includes('echange') || label.includes('échange')) return ['is-exchange', 'Échange'];
  if (label.includes('livraison')) return ['is-delivery', 'Livraison'];
  return ['is-neutral', item.type_mouvement || 'Mouvement'];
};

// Stat secondaire compacte sous le hero : même navigation que les anciennes cartes KPI.
const SubStat = ({ icon, title, main, unit, detail, tone, onClick }) => <button type="button" className={`mobile-substat ${tone}`} onClick={onClick}><span className="mobile-substat-icon">{icon}</span><small>{title}</small><strong>{main}{unit ? <i>{unit}</i> : null}</strong><em>{detail}</em></button>;

export default function MobileCockpitV2() {
  const domain = config.REACT_APP_SERVER_DOMAIN;
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user?.currentUser);
  const role = currentUser?.role;
  const token = currentUser?.accessToken || currentUser?.token;
  const controllerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Période portée par l'URL : lue au montage (défaut today), reflétée à chaque changement.
  const period = PERIODS.some((item) => item.key === searchParams.get('period')) ? searchParams.get('period') : 'today';
  const setPeriod = useCallback((key) => {
    const next = new URLSearchParams(searchParams);
    next.set('period', key);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [todayGroup, setTodayGroup] = useState('variant');
  const [data, setData] = useState(EMPTY);
  const [rotation, setRotation] = useState(EMPTY_ROTATION);
  const [insights, setInsights] = useState(EMPTY_INSIGHTS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [error, setError] = useState('');
  // Compteurs santé stock remontés par MobileStockHealth (déjà chargés par lui,
  // aucun nouvel appel réseau) : clé de période incluse pour ignorer un instantané périmé.
  const [healthSnapshot, setHealthSnapshot] = useState(null);
  const onHealthSnapshot = useCallback((key, snapshot) => setHealthSnapshot({ key, summary: snapshot?.summary || {} }), []);
  // Dernières réponses par période (même modèle que MobileStockHealth) :
  // le changement d'onglet réaffiche instantanément le cache puis rafraîchit en silence.
  const cacheRef = useRef(new Map());
  const dates = useMemo(() => rangeFor(period), [period]);
  const cacheKey = `${dates.start_date}|${dates.end_date}`;
  const headers = useMemo(() => token ? { authorization: `Bearer ${token}`, token: `Bearer ${token}` } : {}, [token]);
  const openMobile = useCallback((section, extra = '') => navigate(`/mobile?section=${section}&period=${period}${extra}`), [navigate, period]);
  const openVariantDetail = useCallback((variantId) => navigate(`/mobile?section=stock&period=${period}&detail=variante:${variantId}`), [navigate, period]);

  const load = useCallback(async (silent = false) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const [overviewResult, rotationResult, insightsResult] = await Promise.allSettled([
        axios.get(`${domain}/api/mobile-cockpit/overview`, { params: dates, headers, signal: controller.signal }),
        axios.get(`${domain}/api/mobile-cockpit/rotation`, { params: dates, headers, signal: controller.signal }),
        axios.get(`${domain}/api/mobile-cockpit/insights`, { params: dates, headers, signal: controller.signal }),
      ]);
      if (controllerRef.current !== controller) return;
      if (overviewResult.status !== 'fulfilled') throw overviewResult.reason;
      const nextData = { ...EMPTY, ...overviewResult.value.data, summary: { ...EMPTY.summary, ...(overviewResult.value.data.summary || {}) } };
      const nextRotation = rotationResult.status === 'fulfilled' ? { ...EMPTY_ROTATION, ...rotationResult.value.data } : EMPTY_ROTATION;
      const nextInsights = insightsResult.status === 'fulfilled' ? { ...EMPTY_INSIGHTS, ...insightsResult.value.data, today: { ...EMPTY_INSIGHTS.today, ...(insightsResult.value.data.today || {}) } } : EMPTY_INSIGHTS;
      const savedAt = Date.now();
      cacheRef.current.set(cacheKey, { savedAt, data: nextData, rotation: nextRotation, insights: nextInsights });
      setData(nextData);
      setRotation(nextRotation);
      setInsights(nextInsights);
      setLastUpdatedAt(savedAt);
      setError('');
    } catch (requestError) {
      if (requestError?.code !== 'ERR_CANCELED' && controllerRef.current === controller) {
        const status = requestError?.response?.status;
        setError(status === 401 || status === 403 ? 'Accès réservé à l’administrateur.' : 'Les données du cockpit ne sont pas disponibles pour le moment.');
      }
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [cacheKey, dates, domain, headers]);

  // Chargement par période : le cache mémoire s'affiche immédiatement, puis un
  // rafraîchissement silencieux se fait en arrière-plan.
  useEffect(() => {
    if (role !== 'admin') return undefined;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setData(cached.data);
      setRotation(cached.rotation);
      setInsights(cached.insights);
      setLastUpdatedAt(cached.savedAt);
      setError('');
      setLoading(false);
      load(true);
    } else {
      load();
    }
    return () => controllerRef.current?.abort();
  }, [cacheKey, load, role]);

  // Polling doux : toutes les 90 s, suspendu quand l'onglet est caché,
  // avec rechargement immédiat au retour si les données ont plus de 90 s.
  useEffect(() => {
    if (role !== 'admin') return undefined;
    let timer = null;
    const stop = () => { if (timer !== null) { window.clearInterval(timer); timer = null; } };
    const start = () => { if (timer === null && !document.hidden) timer = window.setInterval(() => load(true), POLL_MS); };
    const onVisibilityChange = () => {
      if (document.hidden) { stop(); return; }
      const entry = cacheRef.current.get(cacheKey);
      if (!entry || Date.now() - entry.savedAt >= POLL_MS) load(true);
      start();
    };
    start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibilityChange); };
  }, [cacheKey, load, role]);

  if (role !== 'admin') return <Navigate to="/" replace />;

  const { commands, in_transit: transit, pending_outcome: pending, confirmed_sales: sales, returns, exchanges, low_stock: lowStock, on_trial: onTrial = {}, cash_today: cashToday = {}, corrections_today: correctionsToday = 0 } = data.summary;
  // Bloc « temps réel » partagé avec l'accueil : jour vs moyenne + fluidité (toujours
  // ancré sur AUJOURD'HUI, indépendant de l'onglet période).
  const tr = data.temps_reel || null;
  const trCmd = tr?.commandes || {};
  const trJour = tr?.jour || {};
  const trLiv = tr?.en_livraison || {};
  const trFlu = tr?.fluidite || {};
  const trDelta = trCmd.moy_mois > 0 ? Math.round(((trCmd.jour - trCmd.moy_mois) / trCmd.moy_mois) * 100) : null;
  const trLivreurs = Array.isArray(tr?.par_livreur?.livreurs) ? tr.par_livreur.livreurs : [];
  const activities = Array.isArray(data.activities) ? data.activities : [];
  const items = Array.isArray(data.low_stock) ? data.low_stock : [];
  const rotationItems = Array.isArray(rotation.items) ? rotation.items : [];
  const rotationSummary = rotation.summary || {};
  const today = insights.today || {};
  const todayVariants = Array.isArray(today.variants) ? today.variants : [];
  const todayModels = Array.isArray(today.models) ? today.models : [];
  const todayItems = todayGroup === 'variant' ? todayVariants : todayModels;
  const trend = Array.isArray(insights.period_trend) ? insights.period_trend : [];
  const unresolved = n(pending.pairs);
  const exchangeGap = n(exchanges.unreconciled_pairs);
  const negative = n(lowStock.negative_stock_count);
  const periodLabel = PERIODS.find((item) => item.key === period)?.label || "Aujourd'hui";
  const healthSummary = healthSnapshot && healthSnapshot.key === cacheKey ? healthSnapshot.summary : {};
  const priorityRestock = n(rotationSummary.priority_count);
  const recentOutOfStock = n(healthSummary.recent_out_of_stock_count);
  // « À décider aujourd'hui » : uniquement des compteurs déjà calculés par
  // overview (stock négatif), rotation (réassorts prioritaires) et stock-health
  // (ruptures avec demande récente). Aucun nouvel endpoint.
  const decisions = [
    // Commandes créées jamais affectées à un livreur (backlog depuis la bascule
    // livraison V2). Champ additif de l'API : absent (vieille API) → carte masquée.
    n(trCmd.sans_livreur) > 0 && {
      key: 'sans-livreur',
      tone: 'is-gold',
      icon: <CarOutlined />,
      label: 'Commandes sans livreur',
      description: 'Créées mais pas encore affectées : à confier à un livreur.',
      count: n(trCmd.sans_livreur),
      action: () => openMobile('commandes', '&status=sans-livreur'),
    },
    negative > 0 && {
      key: 'negative-stock',
      tone: 'is-critical',
      icon: <WarningOutlined />,
      label: 'Stock négatif à corriger',
      description: 'Le stock réel ne peut pas être négatif : vérifier le mouvement concerné.',
      count: negative,
      action: () => openMobile('stock', '&status=negative'),
    },
    priorityRestock > 0 && {
      key: 'restock-priority',
      tone: 'is-gold',
      icon: <ThunderboltOutlined />,
      label: 'Réassort prioritaire à lancer',
      description: 'Rotation rapide confirmée avec au plus une paire en stock.',
      count: priorityRestock,
      action: () => openMobile('stock', '&status=alert'),
    },
    recentOutOfStock > 0 && {
      key: 'recent-out',
      tone: 'is-gold',
      icon: <FireOutlined />,
      label: 'Rupture après demande récente',
      description: 'Des paires encore demandées ces derniers jours sont épuisées.',
      count: recentOutOfStock,
      action: () => openMobile('stock', '&status=out'),
    },
  ].filter(Boolean);

  return <section className="mobile-cockpit-page ndoe-with-bottom-nav"><div className="mobile-cockpit-shell">
    <header className="mobile-cockpit-header"><div className="mobile-cockpit-brand"><img src={logoNdoe} alt="Ndoé Boutique" className="mobile-cockpit-logo" /><div><div className="mobile-brand-row"><h1>Ndoé App</h1><Tag className="mobile-live-tag">EN DIRECT</Tag></div><p>{periodLabel} · actualisation automatique toutes les 90 secondes</p></div></div><div className="mobile-cockpit-header-actions"><Tooltip title="Messages WhatsApp & fidélité"><Button className="mobile-refresh-button" shape="circle" onClick={() => openMobile('messages')} icon={<WhatsAppOutlined />} /></Tooltip><Tooltip title="Actualiser"><Button className="mobile-refresh-button" shape="circle" onClick={() => load(true)} loading={refreshing} disabled={loading} icon={<ReloadOutlined />} /></Tooltip></div></header>
    <div className="mobile-range-tabs" role="tablist" aria-label="Période du cockpit">{PERIODS.map((item) => <button key={item.key} type="button" role="tab" aria-selected={period === item.key} className={period === item.key ? 'active' : ''} onClick={() => setPeriod(item.key)}>{item.label}</button>)}</div>
    <div className="mobile-scope-hint"><InfoCircleOutlined /><span>Les cartes ouvrent des vues adaptées au téléphone. Les écrans PC restent inchangés.</span></div>
    {error && <Alert className="mobile-cockpit-alert" showIcon type="warning" message={error} />}
    {loading ? <div className="mobile-cockpit-skeleton" aria-busy="true" aria-label="Chargement du cockpit">
      <SkeletonCard variant="panel" className="mobile-hero-skeleton" />
      <SkeletonCard variant="panel" />
      <div className="mobile-substat-grid">{['sub-1', 'sub-2', 'sub-3'].map((key) => <SkeletonCard key={key} variant="kpi" className="mobile-substat-skeleton" />)}</div>
      <SkeletonCard variant="panel" />
      <SkeletonCard variant="panel" />
    </div> : <>
      <button type="button" className="mobile-sales-hero" onClick={() => openMobile('ventes')} aria-label={`Ventes confirmées : ${integer(sales.pairs)} paires, ${usd(sales.amount)}. Ouvrir le détail des ventes.`}>
        <span className="mobile-panel-kicker">NDOÉ · VENTES CONFIRMÉES</span>
        <span className="mobile-hero-figure">{integer(sales.pairs)}<em>paires</em></span>
        <span className="mobile-hero-amount">{usd(sales.amount)}</span>
        <span className="mobile-hero-caption">{integer(sales.count)} commande(s) confirmée(s) · {periodLabel}</span>
        <span className="mobile-hero-foot"><span className="mobile-hero-pulse" aria-hidden="true" />{lastUpdatedAt ? `Actualisé à ${clockTime(lastUpdatedAt)}` : 'Actualisation automatique toutes les 90 s'}<RightOutlined className="mobile-hero-arrow" /></span>
      </button>
      {tr && <section className="mobile-decision-panel" aria-label="Aujourd'hui vs moyenne">
        <span className="mobile-panel-kicker">AUJOURD’HUI VS MOYENNE</span>
        <h2>Où j’en suis aujourd’hui</h2>
        <div className="mobile-tr-rows">
          <div className="mobile-tr-row">
            <span className="mobile-tr-label">Commandes créées</span>
            <span className="mobile-tr-value"><b>{integer(trCmd.jour)}</b>{trDelta != null && <em style={{ color: trDelta >= 0 ? '#2c7a4b' : '#c0392b', fontStyle: 'normal', marginLeft: 6 }}>{trDelta >= 0 ? '+' : ''}{trDelta}%</em>}</span>
          </div>
          <div className="mobile-tr-sub">Moy. mois : {trCmd.moy_mois == null ? '—' : `${trCmd.moy_mois}/j`} · mois dernier : {trCmd.moy_mois_prec == null ? '—' : `${trCmd.moy_mois_prec}/j`}</div>
          <div className="mobile-tr-row">
            <span className="mobile-tr-label">Vendu / commandé (auj.)</span>
            <span className="mobile-tr-value"><b>{integer(trJour.paires_vendues)}</b> / {integer(trJour.paires_commandees)} paires</span>
          </div>
          <div className="mobile-tr-row">
            <span className="mobile-tr-label">Encaissé / attendu (auj.)</span>
            <span className="mobile-tr-value"><b>{usd(trJour.encaisse)}</b> / {usd(trJour.montant_attendu)}</span>
          </div>
          <div className="mobile-tr-row">
            <span className="mobile-tr-label">En livraison (prises en charge)</span>
            <span className="mobile-tr-value"><b>{integer(trLiv.commandes_confirmees)}</b> cmd · {integer(trLiv.paires_confirmees)} paires</span>
          </div>
          <div className="mobile-tr-sub">
            Fluidité : prise en charge {trFlu.prise_en_charge_h == null ? '—' : `${trFlu.prise_en_charge_h} h`} · livraison {trFlu.livraison_h == null ? '—' : `${trFlu.livraison_h} h`} · taux {trFlu.taux_livraison_pct == null ? '—' : `${trFlu.taux_livraison_pct} %`} · reports 7 j : {integer(trFlu.reports_7j)} · en retard : {integer(trFlu.en_retard)}
          </div>
          {trLivreurs.length > 0 && <>
            <div className="mobile-tr-sub" style={{ marginTop: 6, fontWeight: 600, color: '#41506a' }}>Par livreur — aujourd’hui</div>
            {trLivreurs.map((l) => (
              <div className="mobile-tr-liv" key={l.id}>
                <div className="mobile-tr-row">
                  <span className="mobile-tr-label" style={{ fontWeight: 600, color: '#14243b' }}>{l.nom}</span>
                  <span className="mobile-tr-value"><b>{integer(l.paires_vendues)}</b> vendues · {usd(l.ca)}</span>
                </div>
                <div className="mobile-tr-sub">
                  Encaissé {usd(l.encaisse)}{l.ca > l.encaisse ? ` (écart ${usd(l.ca - l.encaisse)})` : ''} · {l.commandes_jour != null ? `${integer(l.commandes_jour)} cmd auj. · ` : ''}{integer(l.commandes_en_cours)} cmd en cours · sorties auj. {integer(l.paires_sorties_jour)} · dehors {integer(l.paires_dehors)}{l.paires_essai > 0 ? ` (dont ${integer(l.paires_essai)} à l’essai)` : ''}
                </div>
                {(l.flu_pec_h != null || l.flu_livraison_h != null || l.flu_taux_pct != null) && (
                  <div className="mobile-tr-sub">
                    Fluidité 7 j : PEC {l.flu_pec_h == null ? '—' : `${l.flu_pec_h} h`} · livraison {l.flu_livraison_h == null ? '—' : `${l.flu_livraison_h} h`} · taux {l.flu_taux_pct == null ? '—' : `${l.flu_taux_pct} %`}
                  </div>
                )}
              </div>
            ))}
          </>}
        </div>
      </section>}
      <section className="mobile-decision-panel" aria-label="À décider aujourd'hui">
        <span className="mobile-panel-kicker">À DÉCIDER AUJOURD’HUI</span>
        <h2>Ce qui demande une décision</h2>
        {decisions.length === 0 ? <div className="mobile-decision-empty"><span className="mobile-decision-empty-mark"><CheckOutlined /></span><div><strong>Situation maîtrisée</strong><p>Aucune action urgente : stock et réassorts sont sous contrôle sur cette période.</p></div></div> : <div className="mobile-decision-list">
          {decisions.map((item) => <button key={item.key} type="button" className={`mobile-decision-item ${item.tone}`} onClick={item.action}>
            <span className="mobile-decision-icon">{item.icon}</span>
            <span className="mobile-decision-copy"><strong>{item.label}</strong><small>{item.description}</small></span>
            <b className="mobile-decision-count">{integer(item.count)}</b>
            <RightOutlined className="mobile-decision-arrow" />
          </button>)}
        </div>}
      </section>
      <div className="mobile-substat-grid">
        <SubStat icon={<ShoppingCartOutlined />} title="Commandes créées" main={integer(commands.count)} detail={`${integer(commands.pairs)} paires · ${usd(commands.amount)}`} tone="tone-blue" onClick={() => openMobile('commandes')} />
        <SubStat icon={<CarOutlined />} title="En circulation prouvée" main={integer(transit.pairs)} unit="paires" detail={`${integer(transit.count)} commandes · ${usd(transit.amount)}`} tone="tone-amber" onClick={() => openMobile('livraisons')} />
        <SubStat icon={<SwapOutlined />} title="Retours & échanges" main={integer(returns.pairs)} unit="paires" detail={`Livraison ${integer(returns.delivery_pairs)} · cliente ${integer(returns.customer_pairs)} · +${integer(exchanges.incoming_pairs)} / −${integer(exchanges.outgoing_pairs)}`} tone="tone-purple" onClick={() => openMobile('retours')} />
        <SubStat icon={<CarOutlined />} title="Paires à l’essai" main={integer(onTrial.pairs)} unit="paires" detail={`${integer(onTrial.orders)} commande(s) · choix cliente en attente`} tone="tone-amber" onClick={() => openMobile('livraisons')} />
        <SubStat icon={<CheckOutlined />} title="Encaissé (période)" main={usd(cashToday.collected)} detail={`Reste à encaisser ${usd(cashToday.outstanding)}`} tone="tone-blue" />
        <SubStat icon={<WarningOutlined />} title="Corrections / régul." main={integer(correctionsToday)} detail="Sur la période · ouvrir le journal" tone="tone-purple" onClick={() => navigate('/journal-modifications')} />
      </div>
      {unresolved > 0 && <div className="mobile-operational-note"><WarningOutlined /><div><strong>{integer(unresolved)} paires à qualifier</strong><span>Historique sans vente, retour ou départ prouvé. Elles ne sont pas affichées comme “chez le livreur”.</span></div><Button type="link" onClick={() => openMobile('livraisons')}>Voir</Button></div>}
      {exchangeGap > 0 && <div className="mobile-operational-note mobile-operational-note-purple"><SwapOutlined /><div><strong>{integer(exchangeGap)} paire(s) d’échange à rapprocher</strong><span>Une entrée ou une sortie manque dans l’historique d’échange.</span></div><Button type="link" onClick={() => openMobile('echanges')}>Voir</Button></div>}
      <Card className="mobile-panel mobile-today-sales" bordered={false}><div className="mobile-panel-head"><div><span className="mobile-panel-kicker">AUJOURD’HUI</span><h2>Ventes du jour</h2><p>{integer(today.net_pairs)} paires nettes · {integer(today.gross_pairs)} ventes confirmées · {integer(today.customer_returns)} retour(s) cliente</p></div><Button type="link" onClick={() => navigate('/mobile?section=ventes&period=today')}>Voir les ventes du jour</Button></div><div className="mobile-insight-tabs" role="tablist" aria-label="Regroupement des ventes du jour"><button type="button" role="tab" aria-selected={todayGroup === 'variant'} className={todayGroup === 'variant' ? 'is-active' : ''} onClick={() => setTodayGroup('variant')}>Par variante</button><button type="button" role="tab" aria-selected={todayGroup === 'model'} className={todayGroup === 'model' ? 'is-active' : ''} onClick={() => setTodayGroup('model')}>Par modèle</button></div><div className="mobile-insight-list">{todayItems.length === 0 ? <span className="mobile-empty-text">Aucune vente nette confirmée aujourd’hui.</span> : todayItems.map((item) => todayGroup === 'variant' ? <button type="button" key={item.id_varianteProduit} onClick={() => openVariantDetail(item.id_varianteProduit)}><span><strong>{item.nom_produit}</strong><small>{[item.couleur, item.taille ? `P${item.taille}` : null].filter(Boolean).join(' · ')}</small></span><b>{integer(item.net_pairs)}<em>paire(s)</em></b><RightOutlined /></button> : <div key={item.id_produit} className="mobile-insight-model-row"><span><strong>{item.nom_produit}</strong><small>{integer(item.variants_count)} variante(s) vendue(s)</small></span><b>{integer(item.net_pairs)}<em>paire(s)</em></b></div>)}</div></Card>
      {data.objectif_mois?.actif && (
        <Card className="mobile-panel" bordered={false}>
          <div className="mobile-panel-head"><div>
            <span className="mobile-panel-kicker">MOIS EN COURS</span>
            <h2>Objectif de ventes</h2>
          </div></div>
          <ObjectifMois o={data.objectif_mois} />
        </Card>
      )}

      {data.balance_agee && (
        <Card className="mobile-panel" bordered={false}>
          <div className="mobile-panel-head"><div>
            <span className="mobile-panel-kicker">ÉTAT ACTUEL</span>
            <h2>Balance âgée des créances</h2>
            <p>L'argent dehors, classé par ancienneté — les plus vieilles à relancer d'abord.</p>
          </div><Button type="link" onClick={() => navigate('/creances')}>Centre des créances</Button></div>
          <BalanceAgee b={data.balance_agee} onOuvrirCreances={() => navigate('/creances')} />
        </Card>
      )}

      {data.marge_periode && (
        <Card className="mobile-panel" bordered={false}>
          <div className="mobile-panel-head"><div>
            <span className="mobile-panel-kicker">{periodLabel.toUpperCase()}</span>
            <h2>Marge brute & panier moyen</h2>
          </div></div>
          <MargeBrute m={data.marge_periode} fenetre={periodLabel} />
        </Card>
      )}

      {data.recettes_clientes && (
        <Card className="mobile-panel" bordered={false}>
          <div className="mobile-panel-head"><div>
            <span className="mobile-panel-kicker">{periodLabel.toUpperCase()}</span>
            <h2>Recettes par cliente</h2>
            <p>Ventes confirmées vs revenus attendus sur la période choisie.</p>
          </div></div>
          {/* La fenêtre du drill-down doit être CELLE du cockpit : sans ces
              bornes, cliquer une cliente affichait 30 jours d'achats sous le
              titre « Aujourd'hui ». */}
          <RecettesClientes rc={data.recettes_clientes} fenetre={periodLabel}
            drillStart={dates.start_date} drillEnd={dates.end_date} />
        </Card>
      )}
      <TodayModelChart items={todayModels} />
      <SalesTrendChart items={trend} />
      <Card className="mobile-panel" bordered={false}><div className="mobile-panel-head"><div><h2><ThunderboltOutlined /> Paires à rotation rapide</h2><p>{integer(rotationSummary.fast_count)} variantes vendues en 30 jours ou moins · {integer(rotationSummary.priority_count)} réassorts prioritaires</p></div></div><div className="mobile-low-stock-list">{rotationItems.length === 0 ? <span className="mobile-empty-text">Aucune rotation rapide fiable sur cette période. Les ventes sans réception FIFO identifiable restent volontairement exclues.</span> : rotationItems.map((item) => <button type="button" key={item.id_varianteProduit} onClick={() => openVariantDetail(item.id_varianteProduit)}><span>{item.nom_produit}</span><small>{[item.couleur, item.taille ? `P${item.taille}` : null].filter(Boolean).join(' · ')} · {integer(item.net_sold)} vendue(s) nettes · Stock {integer(item.stock)} · {item.confidence_label} · {item.recommendation}</small><b>{integer(item.average_days)} j</b></button>)}</div></Card>
      <MobileStockHealth domain={domain} headers={headers} dates={dates} onOpenVariant={openVariantDetail} onSnapshot={onHealthSnapshot} />
      <Card className="mobile-panel" bordered={false}>
      <div className="mobile-panel-head"><div>
      <h2>Activité récente</h2>
      <p>Ventes, réceptions et mouvements avec leur heure d’origine</p>
      </div>
      <Button type="link" onClick={() => openMobile('mouvements')}>Tout voir</Button>
      </div>
      <div className="mobile-activity-list">
      {activities.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune activité récente" /> : activities.map((item) => { const [dot, label] = activityStyle(item); const product = item.nom_produit || item.code_variant || 'Variante non renseignée'; const variant = [item.couleur, item.taille ? `P${item.taille}` : null].filter(Boolean).join(' · '); return <button type="button" className="mobile-activity-item" key={item.event_id} onClick={() => openMobile('mouvements')}>
      <span className={`mobile-activity-dot ${dot}`} />
      <div className="mobile-activity-main">
        <div className="mobile-activity-topline">
          <strong>{label}</strong>
          <time>{time(item.occurred_at)}</time>
          </div>
          <span>
          {product}{variant ? ` · ${variant}` : ''}
          </span><small>{integer(item.quantite)} paire(s){item.client_name ? ` · ${item.client_name}` : ''}</small></div></button>; })}</div></Card>
      <div className="mobile-lower-grid"><Card className="mobile-panel" bordered={false}><div className="mobile-panel-head"><div><h2>Stock à surveiller</h2><p>{integer(lowStock.active_alert_count)} variantes actives · {integer(lowStock.active_out_of_stock_count)} ruptures · {integer(lowStock.active_low_stock_count)} à 1 paire</p></div><WarningOutlined className="mobile-warning-icon" /></div>{negative > 0 && <Alert className="mobile-stock-negative" showIcon type="error" message={`${integer(negative)} stock négatif à corriger immédiatement`} />}<div className="mobile-low-stock-list">{items.length === 0 ? <span className="mobile-empty-text">Aucune alerte active de stock.</span> : items.map((item) => <button type="button" key={item.id_varianteProduit} onClick={() => openMobile('stock', '&status=alert')}><span>{item.nom_produit}</span><small>{[item.couleur, item.taille ? `P${item.taille}` : null].filter(Boolean).join(' · ')}</small><b className={item.stock_status === 'negative' ? 'is-negative' : ''}>{integer(item.stock)}</b></button>)}</div></Card>
      <Card className="mobile-panel" bordered={false}><div className="mobile-panel-head"><div><h2>Accès rapides</h2><p>Consulter les vues adaptées au téléphone</p></div></div><div className="mobile-quick-actions"><button type="button" onClick={() => openMobile('clientes')}><TeamOutlined /> Clientes <RightOutlined /></button><button type="button" onClick={() => openMobile('livreurs')}><CarOutlined /> Livreurs <RightOutlined /></button><button type="button" onClick={() => openMobile('stock', '&status=alert')}><AppstoreOutlined /> Fiches de stock <RightOutlined /></button><button type="button" onClick={() => openMobile('mouvements')}><UnorderedListOutlined /> Mouvements <RightOutlined /></button><button type="button" onClick={() => openMobile('echanges')}><SwapOutlined /> Échanges <RightOutlined /></button></div></Card></div>
    </>}
  </div><BottomNavigation navigate={navigate} active="accueil" /></section>;
}
